/**
 * Formatting helpers shared by every screen (prompt.md §32, §54, §77).
 *
 * WHY shared: token counts, sizes, durations and timestamps appear in the
 * transcript, the usage page, the file list and the schedule list. Formatting
 * them in one place is what keeps "1.2 MB" and "2 min 4 s" consistent across
 * three clients.
 */

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(bytes: number, fractionDigits = 1): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(fractionDigits)} ${BYTE_UNITS[unit] ?? "B"}`;
}

export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) < 1_000) return String(Math.round(value));
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

/** Compact token count; large model budgets are impossible to read in full. */
export function formatTokens(tokens: number): string {
  return `${formatCount(tokens)} ${tokens === 1 ? "token" : "tokens"}`;
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0s";
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.round(ms / 1_000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
}

/** Absolute, unambiguous timestamp. Locale-dependent only in presentation. */
export function formatTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Date(parsed).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Relative time for transcripts and schedules. `now` is injected so a test does
 * not depend on the wall clock.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const deltaMs = now.getTime() - parsed;
  const future = deltaMs < 0;
  const seconds = Math.abs(deltaMs) / 1_000;
  if (seconds < 45) return future ? "in a moment" : "just now";
  const units: readonly { readonly seconds: number; readonly label: string }[] = [
    { seconds: 60, label: "minute" },
    { seconds: 3_600, label: "hour" },
    { seconds: 86_400, label: "day" },
    { seconds: 604_800, label: "week" },
    { seconds: 2_592_000, label: "month" },
  ];
  let chosen = units[0] as { seconds: number; label: string };
  for (const unit of units) {
    if (seconds >= unit.seconds) chosen = unit;
  }
  const amount = Math.max(1, Math.round(seconds / chosen.seconds));
  const label = `${amount} ${chosen.label}${amount === 1 ? "" : "s"}`;
  return future ? `in ${label}` : `${label} ago`;
}

/** Cron expressions are shown next to their timezone, never alone. */
export function formatScheduleExpression(input: { readonly cronExpression: string | null; readonly runAt: string | null; readonly timezone: string }): string {
  if (input.cronExpression) return `${input.cronExpression} (${input.timezone})`;
  if (input.runAt) return `once at ${formatTimestamp(input.runAt)}`;
  return "no fire time configured";
}

export function formatLabel(value: string): string {
  const spaced = value.replace(/[_-]+/g, " ").trim();
  return spaced.length === 0 ? value : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const TIMESTAMP_MODES = ["relative", "absolute", "hidden"] as const;
export type TimestampMode = (typeof TIMESTAMP_MODES)[number];

export function isTimestampMode(value: unknown): value is TimestampMode {
  return typeof value === "string" && (TIMESTAMP_MODES as readonly string[]).includes(value);
}

/**
 * One rendering rule for every client: the user's own mode decides, and a hidden
 * timestamp renders nothing rather than an empty label.
 */
export function renderUserTimestamp(iso: string, mode: TimestampMode, now: Date = new Date()): string | null {
  if (mode === "hidden") return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return mode === "absolute" ? formatTimestamp(iso) : formatRelativeTime(iso, now);
}
